import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

const THRESHOLD = 64;
const MAX_PULL = 88;

function scrollTopOf(el: HTMLElement | null): number {
  if (!el) return 0;
  return el.scrollTop || 0;
}

function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node: HTMLElement | null = el;
  while (node) {
    const style = window.getComputedStyle(node);
    const oy = style.overflowY;
    if ((oy === "auto" || oy === "scroll" || oy === "overlay") && node.scrollHeight > node.clientHeight + 1) {
      return node;
    }
    node = node.parentElement;
  }
  return (document.querySelector(".mobile-body") as HTMLElement | null) ?? el;
}

function ignorePullTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest(
      "input, textarea, select, option, [contenteditable='true'], [role='slider'], input[type=range], .ptt-btn, .leaflet-container, .leaflet-pane, .password-toggle",
    ),
  );
}

export function PullToRefresh({
  enabled,
  onRefresh,
  children,
}: {
  enabled: boolean;
  onRefresh: () => Promise<void>;
  children: ReactNode;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const startY = useRef(0);
  const startX = useRef(0);
  const pulling = useRef(false);
  const busy = useRef(false);
  const offsetRef = useRef(0);
  const refreshRef = useRef(onRefresh);
  const [offset, setOffset] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  refreshRef.current = onRefresh;

  const setPull = useCallback((px: number) => {
    offsetRef.current = px;
    setOffset(px);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const root = rootRef.current;
    if (!root) return;

    const onStart = (ev: TouchEvent) => {
      if (busy.current || ev.touches.length !== 1) return;
      if (ignorePullTarget(ev.target)) return;
      const scroller = findScrollParent(root);
      if (scrollTopOf(scroller) > 2) return;
      pulling.current = true;
      startY.current = ev.touches[0]!.clientY;
      startX.current = ev.touches[0]!.clientX;
    };

    const onMove = (ev: TouchEvent) => {
      if (!pulling.current || busy.current || ev.touches.length !== 1) return;
      const scroller = findScrollParent(root);
      if (scrollTopOf(scroller) > 2) {
        pulling.current = false;
        setPull(0);
        return;
      }
      const touch = ev.touches[0]!;
      const dy = touch.clientY - startY.current;
      const dx = touch.clientX - startX.current;
      if (Math.abs(dx) > Math.abs(dy) + 6) {
        pulling.current = false;
        setPull(0);
        return;
      }
      if (dy <= 0) {
        setPull(0);
        return;
      }
      ev.preventDefault();
      setPull(Math.min(MAX_PULL, dy * 0.55));
    };

    const onEnd = () => {
      if (!pulling.current) return;
      pulling.current = false;
      if (offsetRef.current < THRESHOLD) {
        setPull(0);
        return;
      }
      busy.current = true;
      setRefreshing(true);
      setPull(THRESHOLD);
      void Promise.resolve(refreshRef.current())
        .catch(() => undefined)
        .finally(() => {
          busy.current = false;
          setRefreshing(false);
          setPull(0);
        });
    };

    root.addEventListener("touchstart", onStart, { passive: true });
    root.addEventListener("touchmove", onMove, { passive: false });
    root.addEventListener("touchend", onEnd);
    root.addEventListener("touchcancel", onEnd);
    return () => {
      root.removeEventListener("touchstart", onStart);
      root.removeEventListener("touchmove", onMove);
      root.removeEventListener("touchend", onEnd);
      root.removeEventListener("touchcancel", onEnd);
    };
  }, [enabled, setPull]);

  if (!enabled) return <>{children}</>;

  const show = offset > 4 || refreshing;
  return (
    <div ref={rootRef} className={`ptr-root${refreshing ? " is-refreshing" : ""}`}>
      <div
        className={`ptr-indicator${show ? " is-visible" : ""}`}
        style={{ height: show ? Math.max(offset, refreshing ? THRESHOLD : 0) : 0 }}
        aria-hidden={!show}
      >
        <span className={`ptr-spinner${refreshing || offset >= THRESHOLD ? " is-spin" : ""}`} />
        <span className="ptr-label">{refreshing ? "Refreshing…" : offset >= THRESHOLD ? "Release to refresh" : "Pull to refresh"}</span>
      </div>
      {children}
    </div>
  );
}
